import { useMemo } from 'react';

import {
  ChatCustomView,
  isMessageFromCurrentUser,
  type CustomBubbleProps,
  type ICustomMessage,
} from '@/components/chat';
import { OpeningBubbleMeasure } from '@/components/chat/bubbles/OpeningBubbleMeasure';
import { PrimumSummarySection } from '@/components/chat/bubbles/PrimumSummarySection';
import { hasClinicalCaseToShow } from '@/components/chat/bubbles/caseSummaryState';
import { InlineCitationsProvider } from '@/components/chat/bubbles/references/InlineCitationChip';
import { MessagePublicationsFooter } from '@/components/chat/bubbles/references/MessagePublicationsFooter';
import { ReferenceSheet } from '@/components/chat/bubbles/references/ReferenceSheet';
import {
  buildInlineCitations,
  withOpenableEntries,
} from '@/components/chat/bubbles/references/inlineCitations';
import { buildMessageReferences } from '@/components/chat/bubbles/references/messageReferences';
import { useReferenceSheet } from '@/components/chat/bubbles/references/useReferenceSheet';
import { ChatBubble } from '@/components/chat/message-list/ChatBubble';
import { Box } from '@/components/ui/box';
import { VStack } from '@/components/ui/vstack';
import { useMessageShares } from '@/hooks/chat/useMessagePublications';
import type { useColors } from '@/hooks/useColors';
import { isRecommendedCaseLayout, useCaseLayout } from '@/services/chat/caseLayoutSwitch';
import type { ClinicalCase } from '@/types/clinicalCase';

type Colors = ReturnType<typeof useColors>['colors'];

type DefaultChatBubbleProps = {
  bubbleProps: CustomBubbleProps;
  colors: Colors;
  closedConversation: boolean;
  isAuthor: boolean;
  conversationId: string;
  userId: string;
  /** Constrain the bubble to its content width instead of filling the row. Set
   * when a caption sits above it: a caption pairs its label with a
   * content-width bubble, and a full-width one stretches the column. */
  shrinkWidthToContent?: boolean;
  /** Set on the opening message only: the submitter's case, shown below the text. */
  clinicalCase?: ClinicalCase;
};

/**
 * The ordinary chat bubble — the message's own text, with no variant chrome.
 *
 * Extracted so the one rendering serves both the standalone row and the
 * caption's slot. Those were two hand-built copies that had already drifted:
 * the caption's set `shrinkWidthToContent` and the standalone one did not, so
 * whichever way they were later unified would have silently changed a layout.
 * The prop now carries that difference explicitly.
 *
 * It is also the terminal registry entry — the bubble every message falls back
 * to — so it must stay free of anything type-specific. The reference list is
 * not: publication shares can ride on any message, and this is where a message
 * without extracted statements shows them.
 */
export function DefaultChatBubble({
  bubbleProps,
  colors,
  closedConversation,
  isAuthor,
  conversationId,
  userId,
  shrinkWidthToContent = false,
  clinicalCase,
}: DefaultChatBubbleProps) {
  const message = bubbleProps.currentMessage as ICustomMessage;

  const layout = useCaseLayout();

  const showsCase = hasClinicalCaseToShow(clinicalCase);

  const { publications, shareEntries } = useMessageShares(message);

  const references = useMemo(
    () => buildMessageReferences(shareEntries, publications),
    [shareEntries, publications]
  );

  const referenceSheet = useReferenceSheet({
    references,
    conversationId,
    messageSid: message.messageId,
  });

  const inlineCitations = useMemo(() => {
    const citations = buildInlineCitations(
      message.text ?? '',
      withOpenableEntries(shareEntries, references)
    );

    return citations.length > 0 ? { citations, onOpenCitation: referenceSheet.openCitation } : null;
  }, [message.text, shareEntries, references, referenceSheet.openCitation]);

  return (
    <InlineCitationsProvider value={inlineCitations}>
      <OpeningBubbleMeasure conversationId={conversationId} enabled={!!clinicalCase}>
        <ChatBubble
          fillRow={showsCase && !shrinkWidthToContent}
          outlined={showsCase && isRecommendedCaseLayout(layout)}
          shrinkWidthToContent={shrinkWidthToContent}
          bubbleProps={
            showsCase
              ? {
                  ...bubbleProps,
                  // The case sits under the opening message's text, after its timestamp, in the
                  // same "Summarized by Primum" section the extracted questions use.
                  renderTime: (timeProps) => (
                    <VStack className="w-full items-end pt-2">
                      {bubbleProps.renderTime?.(
                        timeProps as Parameters<NonNullable<typeof bubbleProps.renderTime>>[0]
                      )}
                      <PrimumSummarySection
                        position={bubbleProps.position ?? 'left'}
                        conversationId={conversationId}
                        clinicalCase={clinicalCase}
                      />
                    </VStack>
                  ),
                }
              : bubbleProps
          }
          colors={colors}
          renderCustomContent={(customViewProps) => {
            const customBubbleProps = customViewProps as CustomBubbleProps;

            return (
              <>
                <ChatCustomView
                  {...customBubbleProps}
                  closedConversation={closedConversation}
                  isAuthor={isAuthor}
                  conversationId={conversationId}
                  userId={userId}
                />
                {references.length > 0 ? (
                  <Box className="px-3 pb-2">
                    <MessagePublicationsFooter
                      references={references}
                      position={bubbleProps.position ?? 'left'}
                      onOpenReference={referenceSheet.openReference}
                    />
                  </Box>
                ) : null}
              </>
            );
          }}
        />
      </OpeningBubbleMeasure>
      {references.length > 0 ? (
        <ReferenceSheet
          references={references}
          openIndex={referenceSheet.openIndex}
          onIndexChange={referenceSheet.setIndex}
          onClose={referenceSheet.close}
          onOpenSource={referenceSheet.openSource}
          viewerIsAuthor={isMessageFromCurrentUser(message, userId)}
        />
      ) : null}
    </InlineCitationsProvider>
  );
}
